import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '@app/hooks/reduxHooks'

import { useDeviceData } from '@/hooks'
import { ProfileResponse } from '@/api/profile.api'
import { SelectItemType } from '@/features/separateMap/components/SeparateMap'

type DeviceDataContextType = ReturnType<typeof useDeviceData> & {
  selectedValue: SelectItemType
  setSelectedValue: React.Dispatch<React.SetStateAction<SelectItemType>>
  clickedChip: undefined | string
  setClickedChip: React.Dispatch<React.SetStateAction<undefined | string>>
  profile: ProfileResponse | null
}

const DevicesContext = React.createContext<DeviceDataContextType | null>(null)

export const DevicesProvider = ({ children }: { children: React.ReactNode }) => {
  const { i18n, t } = useTranslation()

  const [clickedChip, setClickedChip] = React.useState<undefined | string>()
  const [selectedValue, setSelectedValue] = React.useState<SelectItemType>({ name: t('main.allDevices'), id: 'all' })

  const navigate = useNavigate()
  const profile = useAppSelector((state) => state.profile.data)

  const { visibleDevices, devicesStatuses, setVisibleDevices, setDevicesStatuses } = useDeviceData({ selectedValue })

  const channelRef = React.useRef<BroadcastChannel | null>(null)

  React.useEffect(() => {
    setSelectedValue((prev) => {
      if (prev?.id === 'all') {
        return {
          ...prev,
          name: t('main.allDevices'),
        }
      }
      return prev
    })
  }, [i18n.language, t])

  React.useEffect(() => {
    const isDetached = localStorage.getItem('isMapDetached') === 'true'
    if (!isDetached || !channelRef.current) return

    try {
      channelRef.current.postMessage({
        type: 'LANGUAGE_CHANGED',
        payload: { lng: i18n.language },
      })
    } catch (error) {
      console.warn('Failed to broadcast LANGUAGE_CHANGED:', error)
    }
  }, [i18n.language])

  React.useEffect(() => {
    const bc = new BroadcastChannel('map-channel')
    channelRef.current = bc

    const handleMessage = (event: MessageEvent) => {
      const { type, payload } = event.data || {}

      switch (type) {
        case 'REQUEST_FULL_DATA':
          try {
            bc.postMessage({
              type: 'DEVICE_DATA_UPDATED',
              payload: {
                visibleDevices,
                devicesStatuses,
                clickedChip,
              },
            })
          } catch (error) {
            console.warn('Failed to respond to REQUEST_FULL_DATA:', error)
          }
          break

        case 'PIN_CLICKED':
          if (payload?.deviceId) {
            navigate(`/device/${payload.deviceId}`)
          }
          break
        case 'CHIP_CLICKED':
          setClickedChip(payload.deviceId)
          break

        case 'SELECT_CHANGED':
          if (payload?.selectedValue) {
            setSelectedValue(payload.selectedValue)
          }
          break

        default:
          break
      }
    }

    bc.addEventListener('message', handleMessage)

    return () => {
      bc.removeEventListener('message', handleMessage)
      bc.close()
      channelRef.current = null
    }
  }, [])

  React.useEffect(() => {
    const isDetached = localStorage.getItem('isMapDetached') === 'true'
    if (!isDetached || !channelRef.current) return

    try {
      channelRef.current.postMessage({
        type: 'DEVICE_DATA_UPDATED',
        payload: {
          visibleDevices,
          devicesStatuses,
          clickedChip,
          selectedValue,
          selectItems: [
            ...(profile?.firms || []),
            { name: t('main.additionalDevices'), id: 'additional' },
            { name: t('main.allDevices'), id: 'all' },
          ],
        },
      })
    } catch (error) {
      console.warn('Failed to broadcast DEVICE_DATA_UPDATED:', error)
    }
  }, [visibleDevices, devicesStatuses, selectedValue])

  return (
    <DevicesContext.Provider
      value={{
        visibleDevices,
        devicesStatuses,
        setVisibleDevices,
        setDevicesStatuses,
        selectedValue,
        setSelectedValue,
        clickedChip,
        setClickedChip,
        profile,
      }}
    >
      {children}
    </DevicesContext.Provider>
  )
}

export const useDeviceDataContext = () => {
  const context = React.useContext(DevicesContext)
  if (!context) {
    throw new Error('useDeviceDataContext must be used within a DeviceDataProvider')
  }
  return context
}
